import {
  RecipeDetail,
  RecipeMainCategoriesQuery,
  RecipeSearchQuery,
} from '@fdc/__generated__/graphql';
import {
  NAVIGATION_CONSTANTS,
  PROD_BASE_URL,
} from '@fdc/constants/navigation.constants';
import { ORGANIZATION_SCHEMA_BLOCK } from '@fdc/constants/organization-schema.constants';
import { ComponentTypes, Content } from '@fdc/repositories/content-repository';
import {
  convertMeasurement,
  getIngredientUnitValueByKey,
} from '@woolworthslimited/core-components-react/ingredients/ingredients.helper';
import { formatDuration } from '@woolworthslimited/core-components-react/shared/lib/time-utils';

export const getRecipeCarosuelJsonLd = (
  title: string,
  recipes: RecipeDetail[]
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    numberOfItems: recipes?.length,
    itemListElement: [
      recipes.map((tile: RecipeDetail, index) => {
        return {
          '@type': 'ListItem',
          'position': index + 1,
          'url': `${PROD_BASE_URL}${NAVIGATION_CONSTANTS.RECIPE}/${tile.slug}`,
        };
      }),
    ],
  };
};

const getIsoDuration = (minutes: number): string | undefined => {
  if (!minutes) {
    return undefined;
  }
  return `PT${formatDuration(minutes).replace(/\s/g, '').toUpperCase()}`;
};

export const getRecipeDetailJsonLd = (recipe: RecipeDetail) => {
  const prepTime = getIsoDuration(recipe.prepTime);
  const cookTime = getIsoDuration(recipe.cookTime);
  const totalTime = getIsoDuration(recipe.totalTime);

  return {
    '@context': 'https://schema.org/',
    '@type': 'Recipe',
    '@id': `${PROD_BASE_URL}${NAVIGATION_CONSTANTS.RECIPE}/${recipe.slug}`,
    name: recipe.recipeTitle,
    image: [recipe.recipeImage],
    ...(recipe.author && {
      author: {
        '@type': 'Organization',
        name: recipe.author,
      },
    }),
    description: recipe.shortDescription,
    ...(prepTime && { prepTime }),
    ...(cookTime && { cookTime }),
    ...(totalTime && { totalTime }),
    recipeYield: `${recipe.serves} servings`,
    ...(recipe.tags.length > 0 && {
      keywords: recipe.tags.map((tag) => tag.displayName).join(', '),
    }),
    recipeCategory: recipe.primaryCategory,
    ...(recipe.nutritionalInfo && {
      nutrition: {
        '@type': 'NutritionInformation',
        calories: `${recipe.nutritionalInfo.energy} kJ`,
        proteinContent: `${recipe.nutritionalInfo.protein}g`,
        fatContent: `${recipe.nutritionalInfo.fatTotal}g`,
        saturatedFatContent: `${recipe.nutritionalInfo.fatSaturated}g`,
        carbohydrateContent: `${recipe.nutritionalInfo.carbohydrates}g`,
        sugarContent: `${recipe.nutritionalInfo.sugars}g`,
        fiberContent: `${recipe.nutritionalInfo.dietaryFibre}g`,
        sodiumContent: `${recipe.nutritionalInfo.sodium}mg`,
      },
    }),
    recipeIngredient: recipe.ingredients.map((ingredient) => {
      const measurement = convertMeasurement(
        `${ingredient.measurement}`,
        ingredient.unit
      );

      const parts = [
        measurement,
        getIngredientUnitValueByKey(ingredient.unit),
        ingredient.ingredientName,
      ].filter(Boolean);

      const ingredientText = parts.join(' ');

      return ingredient.preparation
        ? `${ingredientText}, ${ingredient.preparation}`
        : ingredientText;
    }),
    recipeInstructions: recipe.method.map((step) => ({
      '@type': 'HowToStep',
      text: step.method,
    })),
  };
};

export const getRecipeCollectionJsonLd = (
  pathname: string,
  title: string,
  data?: RecipeSearchQuery,
  pageNumber?: number,
  pageSize?: number,
  disclaimer?: string
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${PROD_BASE_URL}${pathname}`,
    name: title,
    description: disclaimer ?? '',
    mainEntity: {
      '@type': 'ItemList',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: data?.recipes?.totalCount ?? 0,
      itemListElement:
        data?.recipes?.results?.map(
          (tile: RecipeSearchQuery['recipes']['results'][number], index) => {
            return {
              '@type': 'ListItem',
              position: (pageNumber ?? 0) * (pageSize ?? 0) + index + 1,
              url: `${PROD_BASE_URL}${NAVIGATION_CONSTANTS.RECIPE}/${tile.slug}`,
            };
          }
        ) ?? [],
    },
  };
};

export const getContentPageJsonLd = (content: Content, slug: string[]) => {
  const hasRecipeDetail = content.content.some(
    (block) => block.__typename === ComponentTypes.RECIPE_DETAILS
  );
  if (!content.seo?.seoTitle || hasRecipeDetail) {
    return undefined;
  }

  const url = `${PROD_BASE_URL}/${slug.join('/')}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORGANIZATION_SCHEMA_BLOCK.data,
      {
        '@type': 'Article',
        '@id': url,
        headline: content.seo.seoTitle,
        description: content.seo.seoDescription,
        ...(content.seo.seoImage?.url && {
          image: {
            '@type': 'ImageObject',
            url: content.seo.seoImage.url,
          },
        }),
        author: { '@id': PROD_BASE_URL },
        publisher: { '@id': PROD_BASE_URL },
      },
    ],
  };
};

export const getRecipeHubCategoryJsonLd = (
  recipeCategoryData: RecipeMainCategoriesQuery['recipes']['categories']
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${PROD_BASE_URL}${NAVIGATION_CONSTANTS.RECIPES}`,
    name: 'Recipes & Inspiration',
    description: 'Browse free recipes and meal inspiration from Woolworths NZ.',
    mainEntity: {
      '@type': 'ItemList',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: recipeCategoryData.length,
      itemListElement: [
        recipeCategoryData.map((category, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            url: `${PROD_BASE_URL}${NAVIGATION_CONSTANTS.RECIPES}/${category.id}`,
          };
        }),
      ],
    },
  };
};
